import { JAMIE, SARAH, type MockCustomer } from '../../data/mockData.js';

// Same scope as hubspot/src/app/cards/customer-history.ts. A live adapter
// supplies the active, validated scope (see liveSource.ts); it never infers
// authority from a URL.
export interface HistoryScope {
  sessionId: string;
  generation: number;
  workerId: string;
  customerId: string;
}
export type HistoryKind = 'conversation' | 'order' | 'subscription' | 'gift' | 'service' | 'note' | 'preference' | 'horse' | 'letter' | 'call';
export type HistoryCategory = 'all' | HistoryKind;
export type HistoryPeriod = 'all' | 'today' | 'recent';
export interface HistoryEvent {
  id: string;
  customerId: string;
  kind: HistoryKind;
  eventAt: number | null;
  updatedAt?: number;
  receiptAt?: number | null;
  title: string;
  body: string;
  status: string;
  actor: string;
  source: string;
  detail: string;
  timeLabel?: string;
}
export interface HistoryOwner {
  initials: string;
  fullName: string;
}
/**
 * mode
 *   'preview'  the approved Sarah example (only via ?preview= / dev mock)
 *   'live'     HubSpot notes + calls read through /v1/contacts/:id/timeline
 *              on a signed session (W33). Local notes allowed, never written.
 *   'verified' a scoped read-only snapshot supplied by a caller; no local notes.
 */
export type HistorySourceMode = 'preview' | 'verified' | 'live';
export interface HistorySource {
  mode: HistorySourceMode;
  scope: HistoryScope;
  customer: MockCustomer;
  timeZone: string;
  capturedAt: number;
  events: HistoryEvent[];
  /** Who a local note is recorded under (the preview rep, or the signed rep). */
  localNoteAuthor: string;
  /** Owner shown in the OWNERSHIP cell. Never defaults to the preview rep on a live source. */
  owner: HistoryOwner;
  /** Replaces the "Customer since" identity line with a measured fact on a live source. */
  sinceLabel?: string;
}
export interface HistoryReviewState {
  category: HistoryCategory;
  query: string;
  period: HistoryPeriod;
  notes: HistoryEvent[];
}
export interface HistoryNoteDraft {
  text: string;
  noteId: string | null;
  savedText: string;
  feedback: string;
}
export const initialHistoryNoteDraft = (): HistoryNoteDraft => ({ text: '', noteId: null, savedText: '', feedback: '' });
export const HISTORY_CATEGORIES: { key: HistoryCategory; label: string }[] = [
  { key: 'all', label: 'All' }, { key: 'conversation', label: 'Conversations' },
  { key: 'order', label: 'Orders' }, { key: 'subscription', label: 'Subscriptions' },
  { key: 'gift', label: 'Gifts' }, { key: 'service', label: 'Customer Service' },
  { key: 'note', label: 'Notes' }, { key: 'preference', label: 'Preferences' },
  { key: 'horse', label: 'Horses' }, { key: 'letter', label: 'Letters' },
  { key: 'call', label: 'Calls' },
];
/**
 * The four snapshot sections from the approved History design, in order.
 * The third label is verbatim from launch-ready/approved-stack-2026-10-12/
 * 10-History-Consistency-Review-v5.html line 378
 * (`<small>NEXT SUBSCRIPTION</small>`); W35.1 (2026-10-20) renamed it from
 * 'Subscription' per the pixel-parity rule. On a real session that card is
 * fed by the W35 subscription summary (see NextSubscriptionCard.tsx), not
 * by a timeline event.
 */
export type HistorySectionKind = 'conversation' | 'order' | 'subscription' | 'service';
export const HISTORY_SECTIONS: { kind: HistorySectionKind; label: string }[] = [
  { kind: 'conversation', label: 'Latest conversation' },
  { kind: 'order', label: 'Latest order' },
  { kind: 'subscription', label: 'NEXT SUBSCRIPTION' },
  { kind: 'service', label: 'Customer Service' },
];
/**
 * Honest empty copy per section. The order line stays until W29
 * (scope=orders entries) or W34 (order props on the profile) feeds
 * projectOrderScopeEntries / projectProfileOrderProps in liveSource.ts.
 */
export const HISTORY_SECTION_EMPTY: Record<HistorySectionKind, string> = {
  conversation: 'No conversation on file yet',
  order: 'Orders will appear once the Shopify order sync is linked',
  subscription: 'No subscription details on file yet',
  service: 'No customer-service tickets on file',
};
export const LOCAL_NOTE_LABEL = 'Saved in this review only, not written to HubSpot';
export const initialHistoryState = (): HistoryReviewState => ({ category: 'all', query: '', period: 'all', notes: [] });
export const historyScopeKey = (scope: HistoryScope): string =>
  JSON.stringify([scope.sessionId, scope.generation, scope.workerId, scope.customerId]);

export function acceptHistorySource(source: HistorySource | null, expected: HistoryScope | null, routeCustomerId: string): HistorySource | null {
  if (!source || !expected || !expected.sessionId || !expected.workerId || !Number.isSafeInteger(expected.generation) || expected.generation < 1) return null;
  if (expected.customerId !== routeCustomerId || source.customer.id !== routeCustomerId || historyScopeKey(source.scope) !== historyScopeKey(expected)) return null;
  if (!Number.isFinite(source.capturedAt) || source.events.some(event => event.customerId !== routeCustomerId)) return null;
  try { new Intl.DateTimeFormat('en-US', { timeZone: source.timeZone }).format(); } catch { return null; }
  return source;
}

export function historyDateKey(timestamp: number, timeZone: string): string {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(timestamp);
  const value = (type: string): string => parts.find(part => part.type === type)?.value ?? '';
  return `${value('year')}-${value('month')}-${value('day')}`;
}
export function categoryMatches(event: HistoryEvent, category: HistoryCategory): boolean {
  return category === 'all' || event.kind === category || (category === 'conversation' && event.kind === 'call');
}
export function filterHistory(events: HistoryEvent[], state: Pick<HistoryReviewState, 'category' | 'query' | 'period'>, timeZone: string, now: number): HistoryEvent[] {
  const query = state.query.trim().toLocaleLowerCase();
  const today = historyDateKey(now, timeZone);
  const cutoff = new Date(Date.parse(`${today}T12:00:00Z`) - 29 * 86_400_000).toISOString().slice(0, 10);
  return events.filter(event => {
    if (!categoryMatches(event, state.category)) return false;
    if (query && ![event.title, event.body, event.actor, event.source, event.detail, event.status].join(' ').toLocaleLowerCase().includes(query)) return false;
    if (state.period !== 'all') {
      if (event.eventAt === null) return false;
      const date = historyDateKey(event.eventAt, timeZone);
      if (state.period === 'today' ? date !== today : date < cutoff || date > today) return false;
    }
    return true;
  }).sort((a, b) => (b.eventAt ?? -1) - (a.eventAt ?? -1) || a.id.localeCompare(b.id));
}

/** Local notes are allowed on the preview example and on a live source. A verified snapshot stays read-only. */
export function canAddLocalNote(source: HistorySource | null): boolean {
  return !!source && (source.mode === 'preview' || source.mode === 'live');
}

/** Add a dated note that lives only in this review. Nothing is written to HubSpot. */
export function addLocalNote(state: HistoryReviewState, source: HistorySource, body: string, now: number, id: string): HistoryReviewState {
  if (!canAddLocalNote(source) || !body.trim() || !id || !Number.isFinite(now)) return state;
  if (state.notes.some(note => note.id === id)) return state;
  const preview = source.mode === 'preview';
  const note: HistoryEvent = {
    id, customerId: source.scope.customerId, kind: 'note', eventAt: now,
    title: 'Customer note added', body: body.trim(),
    status: preview ? 'Recorded in preview' : LOCAL_NOTE_LABEL,
    actor: source.localNoteAuthor,
    source: preview ? 'Customer notes · Local preview' : 'Customer notes · This review only',
    detail: `This dated note is retained while navigating this review. ${LOCAL_NOTE_LABEL}. It resets when the page reloads.`,
  };
  return { ...state, category: 'all', query: '', period: 'all', notes: [...state.notes, note] };
}

/** Save the editor's local note, keeping one identity across repeated saves.
 * The timeline's original recording time stays intact if that note is edited.
 * This never changes source history or writes a HubSpot note.
 */
export function saveLocalNote(state: HistoryReviewState, source: HistorySource, body: string, now: number, id: string): HistoryReviewState {
  if (!canAddLocalNote(source) || !body.trim() || !id || !Number.isFinite(now)) return state;
  const existing = state.notes.find(note => note.id === id);
  if (!existing) return addLocalNote(state, source, body, now, id);
  if (existing.customerId !== source.scope.customerId || existing.body === body.trim()) return state;
  return { ...state, notes: state.notes.map(note => note.id === id ? { ...note, body: body.trim(), updatedAt: now } : note) };
}

// Structural adapter for CustomerHistoryBuffer.timeline() output. Reuse its
// existing validation/pagination upstream; never relabel approval as delivery or
// receiptAt as the customer event time. This screen does not query that service.
export interface ConversationHistoryRow {
  id: string; direction: 'inbound' | 'outbound'; title: string; body: string;
  subject: string | null; eventAt: number | null; receiptAt: number | null;
  timeLabel: string; details: string[];
}
export function projectConversationHistory(scope: HistoryScope, timeline: { known: ConversationHistoryRow[]; undated: ConversationHistoryRow[] }): HistoryEvent[] {
  return [...timeline.known, ...timeline.undated].map(row => ({
    id: row.id, customerId: scope.customerId, kind: 'conversation', eventAt: row.eventAt,
    receiptAt: row.receiptAt, title: row.title, body: row.body,
    status: row.direction === 'outbound' ? row.details[0] ?? 'Delivery status unavailable' : 'Received',
    actor: row.direction === 'inbound' ? 'Customer' : 'See source record',
    source: 'Workspace conversation history', timeLabel: row.timeLabel,
    detail: [row.subject, row.timeLabel, ...row.details].filter(Boolean).join('\n'),
  }));
}

// Explicit approved example only. Reached only through ?preview= or the dev
// mock session (liveSource.ts reports 'skipped'); unknown route IDs never
// inherit these rows and a real session never sees them.
export function previewHistorySource(customerId: string): HistorySource | null {
  if (customerId !== SARAH.id) return null;
  const event = (id: string, kind: HistoryKind, at: string, title: string, body: string, status: string, actor: string, source: string, detail: string): HistoryEvent =>
    ({ id, customerId, kind, eventAt: Date.parse(at), title, body, status, actor, source: `${source} · Example`, detail });
  return {
    mode: 'preview', scope: { sessionId: 'approved-history-review', generation: 1, workerId: JAMIE.id, customerId },
    customer: SARAH, timeZone: 'America/Chicago', capturedAt: Date.parse('2026-09-30T10:26:00-05:00'),
    localNoteAuthor: JAMIE.fullName, owner: { initials: JAMIE.initials, fullName: JAMIE.fullName },
    events: [
      event('service-100', 'service', '2026-09-30T10:26:00-05:00', 'Automatically routed to Customer Service', 'Sarah reported a leaking Gut X bottle. Ticket EX-CS-1042 was created with her message and the related order details.', 'Awaiting CS acceptance', 'System', 'Help Desk', 'Automatic handling could not complete a remedy under the configured rules. The system recorded ticket EX-CS-1042 and routed it to Customer Service. It tracks acceptance and escalates delays. Sales action: none. No replacement or refund has been confirmed.'),
      event('conversation-1', 'conversation', '2026-09-30T10:18:00-05:00', 'Sarah sent a text', '“Can you confirm when my next Gut X shipment is scheduled?”', 'Reply needed', 'Sarah Mitchell', 'Messaging', 'Inbound text received. A reply has not been sent in this example.'),
      event('subscription-2', 'subscription', '2026-09-29T14:42:00-05:00', 'Subscription date change confirmed', 'Barn supplements · Next order moved from October 12 to October 15.', 'Confirmed', 'System', 'Recharge', 'Previous next-order date: October 12, 2026. Confirmed next-order date: October 15, 2026. Hydration subscription unchanged. This is an order-processing date, not a delivery promise.'),
      event('conversation-3', 'conversation', '2026-09-29T14:36:00-05:00', 'Jamie’s text was delivered', '“I’ll check the date change and follow up once it is confirmed.”', 'Delivered', JAMIE.fullName, 'Messaging delivery event', 'Outbound message sent at 2:35 PM; delivery confirmed at 2:36 PM. No read receipt is shown.'),
      event('horse-4', 'horse', '2026-09-24T11:05:00-05:00', 'Duke’s story updated', 'Sarah shared how she first met Duke and what she enjoys about riding him.', 'Recorded', JAMIE.fullName, 'Horse profile', 'Customer-reported note linked to Duke. This sample illustrates where profile notes appear in the customer timeline.'),
      event('gift-5', 'gift', '2026-09-18T09:22:00-05:00', 'Gift added to a shipment', '100X T-shirt · Navy · L · Attached to the October 15 order.', 'Attached', JAMIE.fullName, 'Gift workflow', 'One-time gift attached to the selected order. It has not shipped and is not a recurring subscription item.'),
      event('order-6', 'order', '2026-09-16T15:14:00-05:00', 'Order EX-1042 delivered', 'Gut X · 2 gallons · Delivery confirmed for Sarah’s barn address.', 'Delivered', 'System', 'Carrier delivery event', 'Carrier reported delivered. Product use and horse-level daily amounts are recorded separately from delivery.'),
      event('call-7', 'call', '2026-08-28T13:10:00-05:00', 'Welcome call completed', 'Sarah and Jamie discussed Duke and how to record his daily routine.', 'Connected', JAMIE.fullName, 'Call record', 'Outcome: connected. Conversation summary recorded by the rep. No recording is included in this design preview.'),
    ],
  };
}
